import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";

import HomePage from "@/pages/HomePage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import { ForgotPasswordPage, ResetPasswordPage } from "@/pages/PasswordPages";
import MembershipPage from "@/pages/MembershipPage";
import PostsListingPage from "@/pages/PostsListingPage";
import PostDetailPage from "@/pages/PostDetailPage";
import { AboutPage, ContactPage } from "@/pages/StaticPages";
import DashboardPage from "@/pages/DashboardPage";
import CheckoutPage from "@/pages/CheckoutPage";
import SupportersPage from "@/pages/SupportersPage";
import LeaderboardPage from "@/pages/LeaderboardPage";
import NotificationsPage from "@/pages/NotificationsPage";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminPosts from "@/pages/admin/AdminPosts";
import AdminPostEditor from "@/pages/admin/AdminPostEditor";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminProofViewer from "@/pages/admin/AdminProofViewer";
import AdminContributions from "@/pages/admin/AdminContributions";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminManualVerify from "@/pages/admin/AdminManualVerify";
import AdminAuditLog from "@/pages/admin/AdminAuditLog";
import AdminDiscordSync from "@/pages/admin/AdminDiscordSync";
import SupportWidget from "@/components/SupportWidget";
import DevAccountSwitcher from "@/components/DevAccountSwitcher";

export default function App() {
  return (
    <BrowserRouter>
      <SettingsProvider>
        <AuthProvider>
          <Toaster position="top-right" theme="dark" toastOptions={{ classNames: { toast: "bg-zinc-950 border border-rose-500/30 text-white" } }} />
          <SupportWidget />
          <DevAccountSwitcher />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/membership" element={<MembershipPage />} />
            <Route path="/posts" element={<PostsListingPage />} />
            <Route path="/posts/:slug" element={<PostDetailPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/supporters" element={<SupportersPage />} />
            <Route path="/leaderboard" element={<LeaderboardPage />} />

            <Route path="/dashboard/*" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/checkout/:tier" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
            <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />

            {/* Standalone full-page admin viewers (outside AdminLayout for max viewport). */}
            <Route path="/admin/payments/:id/proof" element={<ProtectedRoute adminOnly><AdminProofViewer /></ProtectedRoute>} />

            <Route path="/admin" element={<ProtectedRoute adminOnly><AdminLayout /></ProtectedRoute>}>
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="posts" element={<AdminPosts />} />
              <Route path="discord-sync" element={<AdminDiscordSync />} />
              <Route path="posts/new" element={<AdminPostEditor />} />
              <Route path="posts/:id/edit" element={<AdminPostEditor />} />
              <Route path="payments" element={<AdminPayments />} />
              <Route path="contributions" element={<AdminContributions />} />
              <Route path="manual-verify" element={<AdminManualVerify />} />
              <Route path="audit" element={<AdminAuditLog />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </SettingsProvider>
    </BrowserRouter>
  );
}
